import React, { useState, useEffect, useContext } from 'react';
import { firestore } from './firebase-config';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { AuthContext } from './AuthContext';
import './BookReadingPlanner.css';

const BookTracker = () => {
  const { loggedInUser } = useContext(AuthContext);
  const [bookTitle, setBookTitle] = useState('');
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [finishDate, setFinishDate] = useState('2025-12-31');
  const [pagesPerDay, setPagesPerDay] = useState(null);
  const [pagesPerWeek, setPagesPerWeek] = useState(null);
  const [pagesPerMonth, setPagesPerMonth] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Load saved progress from Firestore when the component mounts
  useEffect(() => {
    const loadSavedProgress = async () => {
      if (!loggedInUser || !loggedInUser.uid) {
        setError('Please log in to load your reading plan.');
        return;
      }

      setLoading(true);
      try {
        const userId = loggedInUser.uid;
        const docRef = doc(firestore, 'readingPlans', userId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          setBookTitle(data.bookTitle || '');
          setTotalPages(data.totalPages || 0);
          setCurrentPage(data.currentPage || 0);
          setFinishDate(data.finishDate || '2025-12-31');
        }
      } catch (error) {
        console.error('Error loading progress:', error);
        setError('Failed to load progress. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    loadSavedProgress();
  }, [loggedInUser]);

  // Save progress to Firestore
  const saveProgress = async () => {
    if (!loggedInUser || !loggedInUser.uid) {
      setError('Please log in to save your reading plan.');
      return;
    }

    setLoading(true);
    const userId = loggedInUser.uid;
    const docRef = doc(firestore, 'readingPlans', userId);
    try {
      await setDoc(docRef, {
        bookTitle,
        totalPages,
        currentPage,
        finishDate,
      });
      alert('Progress saved successfully!');
      setError('');
    } catch (error) {
      console.error('Error saving progress:', error);
      setError('Failed to save progress. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Calculate days until the custom deadline
  const calculateDaysUntilDeadline = () => {
    const today = new Date();
    const deadline = new Date(finishDate);
    const timeDifference = deadline - today;
    return Math.max(1, Math.ceil(timeDifference / (1000 * 3600 * 24)));
  };

  // Calculate reading plan
  const calculateReadingPlan = () => {
    if (!bookTitle || totalPages <= 0 || currentPage < 0 || currentPage > totalPages) {
      setError('Please enter valid book details.');
      return;
    }

    const remainingPages = totalPages - currentPage;
    const remainingDays = calculateDaysUntilDeadline();

    const pagesPerDay = (remainingPages / remainingDays).toFixed(2);
    const pagesPerWeek = (remainingPages / (remainingDays / 7)).toFixed(2);
    const pagesPerMonth = (remainingPages / (remainingDays / 30)).toFixed(2);

    setPagesPerDay(pagesPerDay);
    setPagesPerWeek(pagesPerWeek);
    setPagesPerMonth(pagesPerMonth);
    setError('');
  };

  return (
    <div className="book-tracker" style={{ padding: '20px', maxWidth: '600px', margin: 'auto' }}>
      <h1>Book Reading Tracker</h1>
      <p>Enter your book details and get a daily, weekly, and monthly reading plan to finish by your selected date.</p>

      {/* {!loggedInUser && (
        <p style={{ color: 'red' }}>You must be logged in to save or load your reading plan.</p>
      )} */}

      {error && <p style={{ color: 'red' }}>{error}</p>}
      {loading && <p>Loading...</p>}

      <div>
        <label>Book Title:</label>
        <input
          type="text"
          value={bookTitle}
          onChange={(e) => setBookTitle(e.target.value)}
          style={{ display: 'block', marginBottom: '10px', width: '100%' }}
          disabled={!loggedInUser || loading}
        />
      </div>

      <div>
        <label>Total Pages:</label>
        <input
          type="number"
          value={totalPages}
          onChange={(e) => setTotalPages(Number(e.target.value))}
          style={{ display: 'block', marginBottom: '10px', width: '100%' }}
          min="1"
          disabled={!loggedInUser || loading}
        />
      </div>

      <div>
        <label>Current Page:</label>
        <input
          type="number"
          value={currentPage}
          onChange={(e) => setCurrentPage(Number(e.target.value))}
          style={{ display: 'block', marginBottom: '10px', width: '100%' }}
          min="0"
          max={totalPages}
          disabled={!loggedInUser || loading}
        />
      </div>

      <div>
        <label>Finish By (Date):</label>
        <input
          type="date"
          value={finishDate}
          onChange={(e) => setFinishDate(e.target.value)}
          style={{ display: 'block', marginBottom: '10px', width: '100%' }}
          disabled={!loggedInUser || loading}
        />
      </div>

      <button
        onClick={calculateReadingPlan}
        style={{ marginBottom: '10px', width: '100%' }}
        disabled={!bookTitle || totalPages <= 0 || currentPage < 0 || !finishDate || loading}
      >
        Calculate Plan
      </button>

      <button
        onClick={saveProgress}
        style={{ marginBottom: '10px', width: '100%' }}
        disabled={!loggedInUser || !bookTitle || totalPages <= 0 || currentPage < 0 || !finishDate || loading}
      >
        Save Plan
      </button>

      {pagesPerDay && (
        <div>
          <h3>Reading Plan:</h3>
          <p>
            To finish by <b>{finishDate}</b>, read approximately <b>{pagesPerDay}</b> pages per day.
          </p>
          <p>
            To finish by <b>{finishDate}</b>, read approximately <b>{pagesPerWeek}</b> pages per week.
          </p>
          <p>
            To finish by <b>{finishDate}</b>, read approximately <b>{pagesPerMonth}</b> pages per month.
          </p>
        </div>
      )}
    </div>
  );
};

export default BookTracker;